/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action} from 'mobx';

/**************************************** PROJECT MODULES *****************************************/
import {CreateNewRecordStore} from './create-new-record-store';
import {AddNewVaccinationStore} from './add-new-vaccination-store';
import {HomeScreenUIStore} from './home-screen-ui-store';
import {HotkeyStore} from './hotkey-store';
import {ModalStore} from './modal-store';
import {RootStore} from '../root-store';

/********************************************* EXPORT *********************************************/
/**
 * Store data about the current state of the UI - directly store:
 * - Whether scroll outside open modal is active
 *
 * Contains all specialized UI stores, for:
 *     Create new record flow data (createNewRecordStore)
 *     Modals (modalStore)
 *     Home (homeScreenUIStore)
 *     Hotkeys (hotkeyStore)
 *     Add new vaccinations modals
 *         - one for each tab type, with child & adult sharing one (childAdult)
 *
 * Specialized stores handle behaviour such as:
 * - Whether various modals are open or closed (in modalStore)
 * - Content of Create new record flow (createNewRecorStore
 * - Collapse/expand state of sections in home screen (homeScreenUIStore)
 */
export class UIStore {
    public static Instance: UIStore | null = null;

    /**************** Stores ****************/
    public createNewRecordStore: CreateNewRecordStore;
    public homeScreenUIStore: HomeScreenUIStore;
    public hotkeyStore: HotkeyStore;
    public modalStore: ModalStore;

    public addNewVaccinationStores: {
        childAdult: AddNewVaccinationStore;
        travel: AddNewVaccinationStore;
        other: AddNewVaccinationStore;
        all: AddNewVaccinationStore;
    };

    /**************** Scroll ****************/
    /**
     * When false, scrolling outside a currently open modal is prevented
     */
    @observable
    public isOuterScrollActive: boolean = true;

    /**
     * Turn scroll "outer" scroll on and off
     * If false, turn scrollspies off (outside e.g. modal)
     * @param {boolean} newIsOuterScrollActive New val of isOuterScrollActive
     *                                         property
     */
    @action
    public setIsOuterScrollActive = (isOuterScrollActive: boolean) =>
        (this.isOuterScrollActive = isOuterScrollActive);

    /**************** Instantiation ****************/
    /**
     * Factory method (true constructor) for UIStore singleton
     */
    public static new = (rootStore: RootStore) => {
        if (!UIStore.Instance) UIStore.Instance = new UIStore(rootStore);
        return UIStore.Instance;
    };

    private constructor(public rootStore: RootStore) {
        this._initUIStore(rootStore);
    }

    @action
    private _initUIStore(rootStore: RootStore) {
        this.createNewRecordStore = CreateNewRecordStore.new(rootStore);
        this.homeScreenUIStore = new HomeScreenUIStore(rootStore);
        this.hotkeyStore = HotkeyStore.new(rootStore);
        this.addNewVaccinationStores = {
            childAdult: AddNewVaccinationStore.new(rootStore),
            travel: AddNewVaccinationStore.new(rootStore),
            other: AddNewVaccinationStore.new(rootStore),
            all: AddNewVaccinationStore.new(rootStore),
        };
        this.modalStore = ModalStore.new(rootStore);
    }
}
